<script lang="ts">
  import { MobileControls, MobileControlsMarketing } from '$lib';
  import { submitLead } from '$helpers/SubmitLead';
  import { LoadDynamicSection } from 'customer-components/lib/components';
  import { useFormattedProps } from '$helpers/UseFormattedProps';
  import { EditorSectionAction } from '$lib';
  import { browser } from '$app/environment';
  import { onDestroy, onMount } from 'svelte';
  import {
    ArrowUp,
    Pencil,
    ArrowDown,
    scrollToElm,
    Trash,
    RepostIcon,
    Plus,
  } from 'core-components';
  import { env } from '$env/dynamic/public';

  export let landingPageModel,
    pageType,
    pageSettings,
    subscriptionsStatus,
    editorView = false;

  $: blocks = [];
  let isMobile = false;

  //This is only use on mobile and editorView
  let overlayBlocks = {};
  let currentFocusedBlock;

  const getFirstTruthyItem = obj =>
    Object.keys(obj).find((i, index) => index > 0 && obj[i] === true);
  $: {
    if (editorView) {
      loadFont(pageSettings.theme?.fonts);
      currentFocusedBlock = getFirstTruthyItem(overlayBlocks)
        ? {
            ...landingPageModel.sections[getFirstTruthyItem(overlayBlocks)],
            index: getFirstTruthyItem(overlayBlocks),
          }
        : {
            ...landingPageModel.sections[1],
            index: 1,
          };
    }
  }

  function onScroll() {
    if (!browser) return false;
    const threshold = window.innerHeight / 2 - 20;
    const headerIndex = landingPageModel.sections.findIndex(
      x => x.type === 'HeaderContent',
    );
    blocks.forEach((element, index) => {
      if (element) {
        if (index != headerIndex) {
          const rect = element.getBoundingClientRect();
          const containerTop = rect.top;
          const containerBottom = rect.bottom;
          if (containerTop < threshold && containerBottom > threshold) {
            overlayBlocks[index] = true;
          } else {
            overlayBlocks[index] = false;
          }
        } else {
          const rect = element.getBoundingClientRect();
          const containerBottom = rect.bottom;
          if (containerBottom < 120) {
            overlayBlocks[index] = false;
          } else {
            overlayBlocks[index] = true;
          }
        }
      }
    });
  }

  async function loadFont(font) {
    return true;
    await fetch(
      `https://www.googleapis.com/webfonts/v1/webfonts?key=${env.PUBLIC_GOOGLE_API_KEY}&family=${font}`,
    )
      .then(response => response.json())
      .then(data => {
        const fontRegular = new FontFace(
          `${data.items[0].family}`,
          `url(${data.items[0].files['regular']}), url(${data.items[0].files['regular']})`,
          {
            weight: '400',
          },
        );
        const fontBolded = new FontFace(
          `${data.items[0].family}`,
          `url(${data.items[0].files['700']}), url(${data.items[0].files['regular']})`,
          {
            weight: '700',
          },
        );
        fontRegular.load();
        document.fonts.add(fontRegular);

        fontBolded.load();
        document.fonts.add(fontBolded);
      });
  }

  onMount(() => {
    isMobile = window.innerWidth < 768;

    if (editorView) window.addEventListener('scroll', onScroll);

    window.addEventListener('message', event => {
      if (event.data && event.data.type === 'scrollToSection') {
        scrollToElm(blocks[event.data.index], 0.8);
      }
    });

    if (pageType == 'marketing') loadFont(pageSettings.theme?.fonts);
  });
  onDestroy(() => {
    if (browser && editorView) window.removeEventListener('scroll', onScroll);
  });

  const handleEdit = (index: string, id: string) => {
    scrollToElm(blocks[index], 0.8);
    let section = landingPageModel.sections.find(x => x.id === id);
    window.parent.postMessage({ type: 'editSection', section, index }, '*');
  };
  const handleChangeDesign = (index: string, id: string) => {
    let section = landingPageModel.sections.find(x => x.id === id);
    window.parent.postMessage(
      { type: 'changeSectionDesign', section, index },
      '*',
    );
  };
  const handleMoveUp = (index: string, id: string) => {
    let section = landingPageModel.sections.find(x => x.id === id);

    window.parent.postMessage({ type: 'moveSectionUp', section, index }, '*');
    setTimeout(() => {
      scrollToElm(blocks[parseInt(index) - 1], 0.8);
    }, 100);
  };
  const handleMoveDown = (index: string, id: string) => {
    let section = landingPageModel.sections.find(x => x.id === id);
    window.parent.postMessage({ type: 'moveSectionDown', section, index }, '*');
    setTimeout(() => {
      scrollToElm(blocks[parseInt(index) + 1], 0.8);
    }, 100);
  };
  const handleTrash = (index: string, id: string) => {
    let section = landingPageModel.sections.find(x => x.id === id);
    window.parent.postMessage({ type: 'trashSection', section, index }, '*');
  };
  const handleAddSection = (index: string, id: string, position: string) => {
    let section = landingPageModel.sections.find(x => x.id === id);
    index = position === 'before' ? index : (parseInt(index) + 1).toString();
    window.parent.postMessage({ type: 'addSection', section, index }, '*');
  };
</script>

{#if landingPageModel?.sections}
  <main
    class:pb-[120px]={editorView}
    class:sm:pb-[50px]={editorView}
    class="page-render"
    class:marketingPage={pageType === 'marketing'}
    style="{pageType === 'marketing' ? pageSettings.theme?.colors : ''}
     {pageSettings.theme?.fonts?.split('/')[0] &&
    !pageSettings.theme?.fonts?.split('/')[0]?.includes('--')
      ? `--font-family:${pageSettings.theme?.fonts?.split('/')[0]}; --font-family-intertight:${pageSettings.theme?.fonts?.split('/')[0]};`
      : ''}  
    {pageSettings.theme?.fonts?.split('/')[1] &&
    !pageSettings.theme?.fonts?.split('/')[1]?.includes('--')
      ? `--font-family-heading:${pageSettings.theme?.fonts?.split('/')[1]};`
      : ''}  
    "
  >
    {#each landingPageModel.sections.sort((a, b) => a.order - b.order) as section, index (section.id)}
      {@const type = `${section.type}${section.activeVariation ? section.activeVariation : ''}`}
      {@const isMarketingBlock = section.isMarketingBlock}
      <div
        class="relative {section.type === 'MktHeader' &&
        section.activeVariation === 1 &&
        !editorView
          ? 'sticky left-0 right-0 top-0 z-[99999]'
          : ''}"
        bind:this={blocks[index]}
        id={section?.type.replace('Mkt', '').toLowerCase()}
      >
        {#if editorView}
          <EditorSectionAction
            {pageType}
            {section}
            {index}
            {blocks}
            {currentFocusedBlock}
            on:edit={ev => handleEdit(index, section.id)}
            on:changeDesign={ev => handleChangeDesign(index, section.id)}
            on:moveUp={ev => handleMoveUp(index, section.id)}
            on:moveDown={ev => handleMoveDown(index, section.id)}
            on:trash={ev => handleTrash(index, section.id)}
            on:addSection={ev =>
              handleAddSection(index, section.id, ev.detail.position)}
          />
        {/if}
        <LoadDynamicSection
          {isMobile}
          properties={useFormattedProps(section, subscriptionsStatus)}
          settings={isMarketingBlock
            ? {
                ...landingPageModel.index,
                ...pageSettings.mktSettings,
              }
            : pageSettings}
          {isMarketingBlock}
          {type}
        />
      </div>
    {/each}

    {#if editorView}
      <div
        class="border-input fixed right-0 top-[40%] z-50 flex translate-y-[-60%] flex-col items-center justify-center gap-4 rounded-bl-md rounded-tl-md border bg-white p-2 md:hidden"
        style="box-shadow:0px 4px 16px 0px rgba(17, 17, 17, 0.08);"
      >
        <!-- Edit button -->
        <div class="flex flex-col items-center justify-center gap-2">
          <button
            class="border-input rounded-md border bg-[#0594fa] p-2 text-center text-white"
            on:click={() =>
              handleEdit(currentFocusedBlock.index, currentFocusedBlock.id)}
          >
            <Pencil />
          </button>
          <span class="text-xs font-light text-[#0594fa]">Edit</span>
        </div>

        <!-- Change position buttons -->
        {#if currentFocusedBlock.type != 'HeaderContent' && currentFocusedBlock.type != 'Hero' && currentFocusedBlock.type != 'Footer'}
          <div class="flex flex-col items-center justify-center gap-2">
            <button
              disabled={currentFocusedBlock.index <= 2}
              class="border-input rounded-md border bg-white p-2 text-center text-black disabled:opacity-30"
              on:click={() =>
                handleMoveUp(currentFocusedBlock.index, currentFocusedBlock.id)}
            >
              <ArrowUp />
            </button>
            <button
              disabled={currentFocusedBlock.index >= blocks.length - 2}
              class="border-input rounded-md border bg-white p-2 text-center text-black disabled:opacity-30"
              on:click={() =>
                handleMoveDown(
                  currentFocusedBlock.index,
                  currentFocusedBlock.id,
                )}
            >
              <ArrowDown />
            </button>
            <span class="text-xs font-light">Move</span>
          </div>
        {/if}

        <!-- Change design button -->
        {#if currentFocusedBlock.variationMax > 0 && currentFocusedBlock?.type != 'Footer'}
          <div class="flex flex-col items-center justify-center gap-2">
            <button
              class="border-input rounded-md border bg-white p-2 text-center text-black"
              on:click={() =>
                handleChangeDesign(
                  currentFocusedBlock.index,
                  currentFocusedBlock.id,
                )}
            >
              <RepostIcon />
            </button>
            <span class="text-xs font-light">Change</span>
          </div>
        {/if}

        <!-- remove section button -->
        {#if currentFocusedBlock.type !== 'Services' && currentFocusedBlock.type !== 'Benefits' && currentFocusedBlock.type !== 'AboutUs' && currentFocusedBlock.type !== 'Faq' && currentFocusedBlock.type !== 'ContactForm' && currentFocusedBlock.type !== 'Testimonials' && currentFocusedBlock.type !== 'Hero' && currentFocusedBlock.type !== 'HeaderContent' && currentFocusedBlock.type !== 'Footer'}
          <div class="flex flex-col items-center justify-center gap-2">
            <button
              class="border-input rounded-md border bg-white p-2 text-red-500"
              on:click={() =>
                handleTrash(currentFocusedBlock.index, currentFocusedBlock.id)}
            >
              <Trash />
            </button>
            <span class="text-xs font-light">Remove</span>
          </div>
        {/if}

        <!-- Add Section button -->
        <div class="flex flex-col items-center justify-center gap-2">
          <button
            class="border-input rounded-md border bg-white p-2 text-center text-black"
            on:click={() =>
              handleAddSection(
                currentFocusedBlock.index,
                currentFocusedBlock.id,
                currentFocusedBlock.type === 'HeaderContent'
                  ? 'after'
                  : currentFocusedBlock.type === 'Footer'
                    ? 'before'
                    : 'after',
              )}
          >
            <Plus />
          </button>
          <span class="text-xs font-light">Add</span>
        </div>
      </div>
      <div
        class="fixed bottom-[118px] left-0 right-0 z-50 flex h-[62px] items-center justify-center gap-4 bg-[#191a1c] px-4 text-white md:hidden"
      >
        <p class="edit-block-indicator_title">Editing</p>
        <p class="flex-1 rounded-full bg-white p-2 px-4 text-center text-black">
          <span class="text" class:visible={!!currentFocusedBlock.type}
            >{currentFocusedBlock.type}</span
          >
        </p>
      </div>
    {/if}

    {#if pageType === 'default' && !editorView}
      <MobileControls
        properties={{
          ...landingPageModel.sections.find(
            section => section.type === 'HeaderContent',
          )?.model,
          submitLead,
          hero: {
            ...landingPageModel.sections.find(
              section => section.type === 'Hero',
            )?.model,
          },
        }}
        settings={{
          ...landingPageModel.index,
          generalSettings: pageSettings.generalSettings,
        }}
      />
    {:else if pageType === 'marketing' && !editorView && landingPageModel.index?.mktSettings?.stickyButtons.active}
      <MobileControlsMarketing
        settings={{
          ...landingPageModel.index,
          generalSettings: pageSettings.generalSettings,
        }}
      />
    {/if}
  </main>
{/if}
